import { useCallback, useEffect, useRef, useState } from 'react';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { doGetProfile } from '../U2106Service';

type CacheEntry = {
  key: string;
  ssoEnabled: boolean;
};

/** Cache session en mémoire (org + user) pour masquer « Changer mot de passe » si SSO. */
let sessionCache: CacheEntry | null = null;

export function u2106ClearSsoEnabledCache(): void {
  sessionCache = null;
}

export type UseU2106SsoEnabledReturn = {
  /** null = pas encore résolu ; true/false = résultat GET profil. */
  ssoEnabled: boolean | null;
  loading: boolean;
  ensureLoaded: () => void;
};

/**
 * BL-0520 — charge une fois le flag ssoEnabled via GET /U2106/profile.
 * Utilisé pour masquer l'entrée menu « Changer mot de passe ».
 */
export function useU2106SsoEnabled(enabled: boolean): UseU2106SsoEnabledReturn {
  const axios = useU2000AxiosAuthenticated();
  const { auth } = useU2000AuthContext();
  const orgId = auth.user.organizationId ?? '';
  const userName = auth.user.userName ?? '';
  const cacheKey = `${orgId}|${userName}`;

  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(() => {
    if (sessionCache?.key === cacheKey) {
      return sessionCache.ssoEnabled;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (sessionCache?.key === cacheKey) {
      setSsoEnabled(sessionCache.ssoEnabled);
      return;
    }
    setSsoEnabled(null);
  }, [cacheKey]);

  const ensureLoaded = useCallback(() => {
    if (!enabled || !auth.isAuthenticated) {
      return;
    }
    if (sessionCache?.key === cacheKey) {
      setSsoEnabled(sessionCache.ssoEnabled);
      return;
    }
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    setLoading(true);
    doGetProfile(axios)
      .then((response) => {
        const value = response.data.result.ssoEnabled === true;
        sessionCache = { key: cacheKey, ssoEnabled: value };
        setSsoEnabled(value);
      })
      .catch(() => {
        sessionCache = { key: cacheKey, ssoEnabled: false };
        setSsoEnabled(false);
      })
      .finally(() => {
        inFlight.current = false;
        setLoading(false);
      });
  }, [enabled, auth.isAuthenticated, cacheKey, axios]);

  return { ssoEnabled, loading, ensureLoaded };
}
